/**
 * MessagingApp - Main Container
 * 
 * iMessage-style messaging application with responsive design.
 * 
 * Desktop: Split view (sidebar + message view)
 * Mobile: Single view (list OR messages, not both)
 * 
 * Features:
 * - ConversationList (sidebar on desktop, full screen on mobile)
 * - MessageView (main content)
 * - ConversationHeader with back button on mobile
 * - MessageComposer
 * - TypingIndicator
 * - NewConversationDialog
 * - GroupInfoPanel
 * - ConnectionStatusBanner (offline indicator)
 * - Search functionality with scroll-to-message
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, MessageSquare, MessageSquarePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAvatarCache } from '@/contexts';
import ConversationList from './ConversationList';
import NewConversationDialog from './NewConversationDialog';
import MessageView from './MessageView';
import ConversationHeader from './ConversationHeader';
import MessageComposer from './MessageComposer';
import TypingIndicator from './TypingIndicator';
import GroupInfoPanel from './GroupInfoPanel';
import ConnectionStatusBanner from './ConnectionStatusBanner';
import { useTypingIndicator } from './hooks/useTypingIndicator';
import { useMessageQueue } from './hooks/useMessageQueue';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { useDesign } from '@/contexts/DesignContext';
import { 
  initializeUser, 
  subscribeToUnreadCounts,
  clearUnreadCount,
  getConversationDisplayName,
  getConversationParticipantId,
  getConversation,
  markConversationAsReadManual,
  CONVERSATION_TYPES
} from '@/lib/firestore';

export default function MessagingApp({ 
  user, 
  onBack, 
  isEmbedded = false,
  // Props for external search navigation
  initialConversation = null,
  initialTargetMessage = null,
  initialSearchQuery = '',
  // Props for notification message highlighting (separate from search)
  messageHighlight = null,
  onMessageHighlightComplete,
  // Callback to notify parent of which conversation is being viewed (for notification filtering)
  onActiveConversationChange = null,
  // Callback when highlight animation has been consumed (prevents re-highlighting on remount)
  onHighlightConsumed
}) {
  const { isStudio } = useDesign();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [composerHeight, setComposerHeight] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [initialized, setInitialized] = useState(false);
  const { getCachedAvatar } = useAvatarCache();
  
  // Search state
  const [searchTargetMessage, setSearchTargetMessage] = useState(initialTargetMessage);
  const [activeSearchQuery, setActiveSearchQuery] = useState(initialSearchQuery);

  // Notify parent when active conversation changes (for notification filtering)
  useEffect(() => {
    if (onActiveConversationChange) {
      onActiveConversationChange(selectedConversation?.id || null);
    }
  }, [selectedConversation?.id, onActiveConversationChange]);

  // Update searchTargetMessage when initialTargetMessage changes (e.g., from notification click)
  useEffect(() => {
    if (initialTargetMessage) {
      setSearchTargetMessage(initialTargetMessage);
    }
  }, [initialTargetMessage]);

  // Load full conversation data when initialConversation is provided (just an ID object)
  useEffect(() => {
    const loadInitialConversation = async () => {
      if (initialConversation?.id) {
        // Check if we're already viewing this conversation
        if (selectedConversation?.id === initialConversation.id) {
          // Same conversation, just update the target message
          return;
        }
        
        try {
          // If initialConversation only has an ID, fetch the full conversation
          if (!initialConversation.participants && !initialConversation.type) {
            const fullConversation = await getConversation(initialConversation.id);
            if (fullConversation) {
              setSelectedConversation(fullConversation);
            } else {
              // Conversation not found - likely deleted
              toast.error('This conversation has been deleted', {
                description: 'The message you were looking for is no longer available.'
              });
            }
          } else {
            // Already have full conversation data
            setSelectedConversation(initialConversation);
          }
        } catch (err) {
          console.error('Error loading initial conversation:', err);
          toast.error('This conversation has been deleted', {
            description: 'The message you were looking for is no longer available.'
          });
        }
      }
    };
    loadInitialConversation();
    // Only depend on initialConversation.id to avoid re-running unnecessarily
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversation?.id]);

  // Handle notification highlight — navigate to the right conversation
  useEffect(() => {
    if (!messageHighlight?.conversationId) return;
    
    // If already viewing this conversation, nothing to do — MessageView handles the scroll
    if (selectedConversation?.id === messageHighlight.conversationId) return;
    
    const loadConversation = async () => {
      try {
        const fullConversation = await getConversation(messageHighlight.conversationId);
        if (fullConversation) {
          setSelectedConversation(fullConversation);
        } else {
          if (onMessageHighlightComplete) onMessageHighlightComplete();
        }
      } catch (err) {
        console.error('Error loading conversation for notification highlight:', err);
        if (onMessageHighlightComplete) onMessageHighlightComplete();
      }
    };
    loadConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageHighlight?.conversationId]);

  // Compute the highlight message ID — only when the correct conversation is active
  const highlightMessageId = (
    messageHighlight?.messageId && 
    selectedConversation?.id === messageHighlight?.conversationId
  ) ? messageHighlight.messageId : null;

  // Initialize user in Firestore on mount
  // This syncs the user's current name/avatar from the app to Firestore
  useEffect(() => {
    if (!user?.id) return;

    const init = async () => {
      try {
        // Use the proper display name - check multiple sources
        const displayName = user.name || user.user_metadata?.name || user.user_metadata?.full_name || user.email;
        const avatarUrl = user.avatar || null;
        
        console.log('[MessagingApp] Initializing user in Firestore:', { 
          id: user.id, 
          name: displayName, 
          avatar: avatarUrl 
        });
        
        await initializeUser({
          id: user.id,
          name: displayName,
          email: user.email,
          avatar: avatarUrl
        });
        setInitialized(true);
      } catch (err) {
        console.error('Error initializing user:', err);
        setInitialized(true); // Continue anyway
      }
    };

    init();
  }, [user]);

  // Subscribe to unread counts
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = subscribeToUnreadCounts(user.id, (counts) => {
      setUnreadCounts(counts);
    });

    return () => unsubscribe();
  }, [user?.id]);

  // Handle conversation selection
  const handleSelectConversation = useCallback(async (conversation) => {
    console.log('[MessagingApp] Selecting conversation:', conversation?.id);
    setSelectedConversation(conversation);
    setShowInfoPanel(false);
    setSearchTargetMessage(null);
    setActiveSearchQuery('');
    
    if (user?.id && conversation?.id) {
      try {
        console.log('[MessagingApp] Clearing unread for:', { conversationId: conversation.id, userId: user.id });
        // Clear unread count
        await clearUnreadCount(user.id, conversation.id);
        // Also clear the manually marked unread flag
        await markConversationAsReadManual(conversation.id, user.id);
        console.log('[MessagingApp] Successfully cleared unread flags');
      } catch (err) {
        console.error('[MessagingApp] Error clearing unread count:', err);
      }
    } else {
      console.warn('[MessagingApp] Missing user.id or conversation.id:', { userId: user?.id, conversationId: conversation?.id });
    }
  }, [user?.id]);

  // Handle search result selection
  const handleSearchResultSelect = useCallback(async (conversation, message, query) => {
    setSelectedConversation(conversation);
    setShowInfoPanel(false);
    setSearchTargetMessage(message);
    setActiveSearchQuery(query);
    
    if (user?.id && conversation?.id) {
      try {
        // Clear unread count
        await clearUnreadCount(user.id, conversation.id);
        // Also clear the manually marked unread flag
        await markConversationAsReadManual(conversation.id, user.id);
      } catch (err) {
        console.error('Error clearing unread count:', err);
      }
    }
  }, [user?.id]);

  // Handle external search navigation (from TeamCommsApp unified search)
  // Also clear unread count when initial conversation is provided
  useEffect(() => {
    if (initialConversation && user?.id && initialConversation?.id) {
      clearUnreadCount(user.id, initialConversation.id).catch(err => {
        console.error('Error clearing unread count:', err);
      });
    }
  }, [initialConversation, user?.id]);
  
  // Handle target message highlighting when both conversation and message are provided
  useEffect(() => {
    if (initialConversation && initialTargetMessage) {
      setSearchTargetMessage(initialTargetMessage);
      setActiveSearchQuery(initialSearchQuery || '');
    }
  }, [initialConversation, initialTargetMessage, initialSearchQuery]);

  // Clear search state
  const handleClearSearch = useCallback(() => {
    setSearchTargetMessage(null);
    setActiveSearchQuery('');
  }, []);

  // Handle back button on mobile (deselect conversation)
  const handleMobileBack = useCallback(() => {
    setSelectedConversation(null);
    setShowInfoPanel(false);
    setSearchTargetMessage(null);
    setActiveSearchQuery('');
  }, []);

  // Handle new conversation created
  const handleConversationCreated = useCallback((conversation) => {
    setSelectedConversation(conversation);
    setShowNewConversation(false);
  }, []);

  // Refresh conversation data
  const refreshConversation = useCallback(async () => {
    if (!selectedConversation?.id) return;
    
    try {
      const updated = await getConversation(selectedConversation.id);
      if (updated) {
        setSelectedConversation(updated);
      }
    } catch (err) {
      console.error('Error refreshing conversation:', err);
    }
  }, [selectedConversation?.id]);

  // Handle leaving a group
  const handleLeaveGroup = useCallback(() => {
    setSelectedConversation(null);
    setShowInfoPanel(false);
  }, []);

  // Typing indicator
  const {
    typingText,
    isAnyoneTyping,
    sendTypingIndicator,
    clearTypingIndicator
  } = useTypingIndicator(
    selectedConversation?.id,
    user?.id,
    selectedConversation?.participants || []
  );

  // Clear reply state when conversation changes
  useEffect(() => {
    setReplyingTo(null);
  }, [selectedConversation?.id]);

  // Online status
  const isOnline = useOnlineStatus();

  // Message queue
  const {
    pendingMessages,
    sendText,
    sendImages,
    sendVoice,
    sendVideo,
    sendPoll,
    retryMessage,
    cancelMessage
  } = useMessageQueue(selectedConversation?.id, user, () => {
    console.log('Message sent via queue');
  });

  // Calculate total unread (exclude the currently active conversation)
  const totalUnread = Object.entries(unreadCounts).reduce((sum, [convId, count]) => {
    if (convId === selectedConversation?.id) return sum;
    return sum + count;
  }, 0);

  return (
    <div data-ui="ms-shell" data-thread={selectedConversation ? 'open' : undefined} className={`${isEmbedded ? 'h-full' : 'h-screen'} flex flex-col bg-slate-50 dark:bg-[#1e1f22] overflow-x-hidden max-w-full`}>
      {/* Offline Banner */}
      <ConnectionStatusBanner isOnline={isOnline} />
      
      {/* Header - Hide when embedded (tabs handle navigation) or on mobile when conversation selected */}
      {!isEmbedded && (
        <header className={`bg-card border-b border-slate-200 dark:border-white/10 px-4 py-3 flex items-center gap-4 flex-shrink-0
          ${selectedConversation ? 'hidden md:flex' : 'flex'}
        `}>
          <Button
            variant="ghost"
            size={isStudio ? 'icon' : 'sm'}
            onClick={onBack}
            className="text-slate-500 dark:text-slate-400"
            aria-label={isStudio ? 'Back to dashboard' : undefined}
          >
            <ArrowLeft className={isStudio ? 'w-[18px] h-[18px]' : 'w-4 h-4 mr-1'} />
            {!isStudio && <span className="hidden sm:inline">Back</span>}
          </Button>
          <div className="flex items-center gap-2">
            <div data-tile="comms" className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-white" />
            </div>
            <h1 className={isStudio ? 'text-lg font-semibold tracking-tight text-foreground' : 'text-lg font-bold text-slate-800 dark:text-slate-100'}>Messages</h1>
            {totalUnread > 0 && (
              <span data-ui="count-bad" className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
          </div>
        </header>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden min-w-0">
        {/* 
          Conversation List
          - Desktop: Always visible as sidebar (w-80)
          - Mobile: Full width when no conversation selected, hidden when conversation open
        */}
        <div className={`
          h-full bg-card min-w-0
          ${selectedConversation 
            ? 'hidden md:block md:w-80 md:flex-shrink-0 md:border-r md:border-slate-200' 
            : 'w-full md:w-80 md:flex-shrink-0 md:border-r md:border-slate-200'
          }
        `}>
          <ConversationList
            user={user}
            selectedConversationId={selectedConversation?.id}
            onSelectConversation={handleSelectConversation}
            onSearchResultSelect={handleSearchResultSelect}
            onNewConversation={() => setShowNewConversation(true)}
            unreadCounts={unreadCounts}
          />
        </div>

        {/* 
          Message View
          - Desktop: Always visible, shows empty state or messages
          - Mobile: Full width when conversation selected, hidden otherwise
        */}
        <div className={`
          flex-1 h-full bg-card flex flex-col overflow-hidden
          ${selectedConversation 
            ? 'block' 
            : 'hidden md:flex'
          }
        `}>
          {selectedConversation ? (
            <div className="flex flex-col h-full">
              {/* Conversation Header - Fixed at top using flexbox (not absolute) for stable mobile scrolling */}
              <div className="flex-shrink-0 relative z-0">
                <ConversationHeader
                  conversation={selectedConversation}
                  displayName={getConversationDisplayName(selectedConversation, user.id)}
                  avatar={(() => {
                    if (selectedConversation.type === CONVERSATION_TYPES.GROUP) return null;
                    const otherUserId = selectedConversation.participants?.find(id => id !== user.id);
                    if (!otherUserId) return null;
                    return getCachedAvatar(otherUserId)
                      || selectedConversation.participantDetails?.[otherUserId]?.avatar
                      || null;
                  })()}
                  participantId={getConversationParticipantId(selectedConversation, user.id)}
                  infoOpen={showInfoPanel}
                  onInfoClick={() => setShowInfoPanel(!showInfoPanel)}
                  onBack={handleMobileBack}
                  showBackButton={true}
                />
              </div>
              
              {/* Message View - flex-1 to fill remaining space, handles its own scrolling */}
              <div className="flex-1 flex flex-col min-h-0">
                <MessageView
                  conversation={selectedConversation}
                  currentUserId={user.id}
                  currentUser={user}
                  pendingMessages={pendingMessages}
                  onRetryMessage={retryMessage}
                  onCancelMessage={cancelMessage}
                  searchTargetMessage={searchTargetMessage}
                  searchQuery={activeSearchQuery}
                  onClearSearch={handleClearSearch}
                  onHighlightConsumed={onHighlightConsumed}
                  highlightMessageId={highlightMessageId}
                  onHighlightComplete={onMessageHighlightComplete}
                  composerHeight={composerHeight}
                  onReply={setReplyingTo}
                />
              </div>
              
              {/* Typing Indicator */}
              {isAnyoneTyping && (
                <TypingIndicator text={typingText} />
              )}
              
              {/* Message Composer */}
              <MessageComposer
                conversationId={selectedConversation.id}
                currentUser={user}
                onSendText={(text) => {
                  const reply = replyingTo ? { id: replyingTo.id, text: replyingTo.text || '', senderName: replyingTo.senderName } : null;
                  setReplyingTo(null);
                  return sendText(text, reply);
                }}
                onSendImages={(images, text) => {
                  const reply = replyingTo ? { id: replyingTo.id, text: replyingTo.text || '', senderName: replyingTo.senderName } : null;
                  setReplyingTo(null);
                  return sendImages(images, text, reply);
                }}
                onSendVoice={(blob, dur) => {
                  const reply = replyingTo ? { id: replyingTo.id, text: replyingTo.text || '', senderName: replyingTo.senderName } : null;
                  setReplyingTo(null);
                  return sendVoice(blob, dur, reply);
                }}
                onSendVideo={(video, text) => {
                  const reply = replyingTo ? { id: replyingTo.id, text: replyingTo.text || '', senderName: replyingTo.senderName } : null;
                  setReplyingTo(null);
                  return sendVideo(video, text, reply);
                }}
                onSendPoll={(poll) => {
                  const reply = replyingTo ? { id: replyingTo.id, text: replyingTo.text || '', senderName: replyingTo.senderName } : null;
                  setReplyingTo(null);
                  return sendPoll(poll, reply);
                }}
                replyingTo={replyingTo}
                onCancelReply={() => setReplyingTo(null)}
                onTyping={sendTypingIndicator}
                onStopTyping={clearTypingIndicator}
                onHeightChange={setComposerHeight}
              />
            </div>
          ) : (
            // Desktop empty state (hidden on mobile)
            <div data-ui="empty" className="h-full flex items-center justify-center" data-testid="no-conversation-selected">
              <div className="text-center px-8">
                <div data-ui="empty-glyph" className="w-24 h-24 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mx-auto mb-6 shadow-inner">
                  <MessageSquare className="w-12 h-12 text-slate-400 dark:text-slate-500" />
                </div>
                <h2 data-ui="empty-title" className="text-xl font-semibold text-slate-700 dark:text-slate-200 mb-2">Select a conversation</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-[280px] mx-auto">
                  Choose from your existing conversations on the left, or start a new one to begin messaging.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Group Info Panel - slides in from right */}
        {showInfoPanel && selectedConversation && (
          <GroupInfoPanel
            conversation={selectedConversation}
            currentUser={user}
            onClose={() => setShowInfoPanel(false)}
            onConversationUpdated={refreshConversation}
            onLeaveGroup={handleLeaveGroup}
          />
        )}
      </div>

      {/* Floating Compose Button - Mobile only, when on conversation list (no conversation selected) */}
      {(isEmbedded || isStudio) && !selectedConversation && (
        <button
          onClick={() => setShowNewConversation(true)}
          data-ui="fab"
          data-standalone={!isEmbedded ? 'true' : undefined}
          className="md:hidden fixed right-4 bottom-20 w-14 h-14 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white rounded-full shadow-lg shadow-blue-500/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95 z-20"
          data-testid="mobile-compose-fab"
        >
          <MessageSquarePlus className="w-6 h-6" />
        </button>
      )}

      {/* New Conversation Dialog */}
      <NewConversationDialog
        open={showNewConversation}
        onClose={() => setShowNewConversation(false)}
        user={user}
        onConversationCreated={handleConversationCreated}
      />
    </div>
  );
}
